import { formatCurrency } from '@eriko/shared-types';
import { ArrowDown, ArrowUp, Minus, TrendingUp } from 'lucide-react';
import { formatRange } from './utils';

interface Kpi {
  key: string;
  label: string;
  value: number | null;
  unit: 'USD' | '%' | string;
  priorValue?: number | null;
  deltaPct?: number | null;
  direction?: 'up' | 'down' | 'flat';
  unavailable?: boolean;
  unavailableReason?: string;
}

interface Data {
  type: string;
  mode?: 'qbo' | 'xero' | 'local';
  period: { from: string; to: string };
  priorPeriod?: { from: string; to: string };
  kpis: Kpi[];
}

const INVERTED_KPI_KEYS = new Set([
  'totalExpenses',
  'cogs',
  'operatingExpenses',
  'expenses',
  'churn',
  'burnRate',
  'debt',
  'debtToEquity',
  'daysSalesOutstanding',
  'dso',
  'arDays',
  'apDays',
]);

function formatKpiValue(value: number | null, unit: string): string {
  if (value === null) return '—';
  if (unit === 'USD') return formatCurrency(value, 2);
  if (unit === '%') return `${value.toFixed(2)}%`;
  return String(value);
}

function deltaPolarity(
  direction: 'up' | 'down' | 'flat',
  kpiKey: string,
): 'good' | 'bad' | 'neutral' {
  if (direction === 'flat') return 'neutral';
  const inverted = INVERTED_KPI_KEYS.has(kpiKey);
  const isGood = inverted ? direction === 'down' : direction === 'up';
  return isGood ? 'good' : 'bad';
}

function DeltaPill({
  deltaPct,
  direction,
  kpiKey,
}: {
  deltaPct: number | null;
  direction: 'up' | 'down' | 'flat';
  kpiKey: string;
}) {
  const baseClass =
    'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums';

  if (deltaPct == null) {
    return <span className={`${baseClass} bg-surface-50 text-surface-500`}>new</span>;
  }

  if (direction === 'flat') {
    return (
      <span className={`${baseClass} bg-surface-50 text-surface-500`}>
        <Minus className="h-3 w-3" />
        {Math.abs(deltaPct).toFixed(1)}%
      </span>
    );
  }

  const polarity = deltaPolarity(direction, kpiKey);
  const Arrow = direction === 'up' ? ArrowUp : ArrowDown;
  const tone =
    polarity === 'good'
      ? 'bg-pos-surface text-pos'
      : polarity === 'bad'
        ? 'bg-neg-surface text-neg'
        : 'bg-surface-50 text-surface-500';

  return (
    <span className={`${baseClass} ${tone}`}>
      <Arrow className="h-3 w-3" />
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
}

export function KpiDashboardReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const rangeLabel = formatRange(d.period.from, d.period.to);
  const isEmpty = d.kpis.length === 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <TrendingUp className="h-4.25 w-4.25 text-info" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">
            Key Performance Indicators
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {d.mode === 'local' && (
            <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
              Local ledger
            </span>
          )}
          {rangeLabel && (
            <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
              {rangeLabel}
            </span>
          )}
        </div>
      </div>

      {/* KPI grid */}
      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No KPIs available for this period.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2">
          {d.kpis.map((kpi, i) => {
            const isUnavailable = kpi.unavailable === true || kpi.value === null;
            const isLastRow =
              d.kpis.length % 2 === 0
                ? i >= d.kpis.length - 2
                : i === d.kpis.length - 1;

            return (
              <div
                key={kpi.key}
                className={[
                  'flex flex-col p-4 transition-colors hover:bg-surface-50/40',
                  isLastRow ? '' : 'border-b border-surface-100',
                  'sm:odd:border-r sm:odd:border-surface-100',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-surface-500">
                  {kpi.label}
                </div>
                <div
                  className={`mt-1 font-mono text-xl font-semibold tabular-nums ${
                    isUnavailable ? 'text-surface-400' : 'text-surface-900'
                  }`}
                >
                  {formatKpiValue(kpi.value, kpi.unit)}
                </div>
                {kpi.unavailable && kpi.unavailableReason && (
                  <div className="mt-1 text-[11px] leading-tight text-surface-500">
                    {kpi.unavailableReason}
                  </div>
                )}
                {!isUnavailable && kpi.direction !== undefined && (
                  <div className="mt-1.5">
                    <DeltaPill
                      deltaPct={kpi.deltaPct ?? null}
                      direction={kpi.direction}
                      kpiKey={kpi.key}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
